import React, { useEffect, useState } from 'react';
import { CheckIcon, StarIcon, BrainIcon, ZapIcon, ActivityIcon, AlertCircleIcon } from 'lucide-react';
import Testimonials from './Testimonials';
import ExitPopup from './ExitPopup';
const QuizResults = ({
  userInfo,
  answers
}) => {
  const [showExitPopup, setShowExitPopup] = useState(false);
  // Analyze answers to determine severity
  const analyzeSeverity = () => {
    // Convert answers to severity points (higher options = more severe)
    const severityPoints = Object.values(answers).reduce((total, answer, index) => {
      const question = index + 1;
      const optionIndex = getOptionIndex(question, answer);
      return total + optionIndex;
    }, 0);
    // Calculate average severity (0-3 scale)
    const avgSeverity = severityPoints / Object.keys(answers).length;
    // Return mild if average < 2, otherwise severe
    return avgSeverity < 2 ? 'mild' : 'severe';
  };
  // Helper function to get option index for a question's answer
  const getOptionIndex = (questionId, answer) => {
    const questions = [{
      id: 1,
      options: ['Nästan aldrig', 'Någon gång i månaden', 'Någon gång i veckan', 'Nästan dagligen']
    }, {
      id: 2,
      options: ['Nästan aldrig', 'Ibland, men det är inte ett problem', 'Ofta, det börjar bli besvärligt', 'Mycket ofta, det är frustrerande']
    }, {
      id: 3,
      options: ['Nästan aldrig', 'Ibland när jag är trött', 'Ganska ofta', 'Regelbundet, det påverkar mina samtal']
    }, {
      id: 4,
      options: ['Bättre än förr', 'Ungefär detsamma', 'Något sämre', 'Märkbart sämre']
    }, {
      id: 5,
      options: ['Sällan eller aldrig', 'Ibland', 'Ganska ofta', 'Mycket ofta']
    }];
    const question = questions.find(q => q.id === questionId);
    if (!question) return 0;
    return question.options.findIndex(option => option === answer);
  };
  const severity = analyzeSeverity();
  useEffect(() => {
    window.scrollTo(0, 0);
    // Exit intent detection
    const handleMouseLeave = e => {
      if (e.clientY <= 0 && !showExitPopup) {
        setShowExitPopup(true);
      }
    };
    // Add exit intent detection
    document.addEventListener('mouseleave', handleMouseLeave);
    // Also show popup after 30 seconds if user hasn't left
    const timeoutId = setTimeout(() => {
      if (!showExitPopup) {
        setShowExitPopup(true);
      }
    }, 30000);
    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      clearTimeout(timeoutId);
    };
  }, [showExitPopup]);
  // Featured testimonial
  const featuredTestimonial = {
    name: 'Anna J.',
    occupation: 'Läkare, 54 år',
    text: 'Efter 3 veckor med Brainy kan jag nu minnas komplexa detaljer igen – det känns som att jag fått tillbaka min gamla hjärna.'
  };
  return <div className="container mx-auto px-4 py-8" data-id="element-179">
      <style jsx global data-id="element-180">{`
        @keyframes pulse-animation {
          0% {
            box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.7);
            transform: scale(1);
          }
          50% {
            box-shadow: 0 0 0 10px rgba(37, 99, 235, 0);
            transform: scale(1.03);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(37, 99, 235, 0);
            transform: scale(1);
          }
        }
        @keyframes color-pulse {
          0% {
            background-color: #2563eb;
          }
          50% {
            background-color: #1d4ed8;
          }
          100% {
            background-color: #2563eb;
          }
        }
        .pulse-button {
          animation:
            pulse-animation 2s infinite,
            color-pulse 3s infinite;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
      <ExitPopup isVisible={showExitPopup} onClose={() => setShowExitPopup(false)} data-id="element-181" />
      <div className="max-w-4xl mx-auto" data-id="element-182">
        <div className="bg-white rounded-lg shadow-lg p-6 md:p-8 mb-8" data-id="element-183">
          <div className="text-center mb-8" data-id="element-184">
            <div className="inline-block bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold mb-4" data-id="element-185">
              Din Minnesanalys
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4" data-id="element-186">
              Vi har analyserat dina svar – här är vad vi hittade
            </h1>
            <p className="text-lg text-gray-600" data-id="element-187">
              {severity === 'mild' ? 'Dina svar visar tidiga signaler på att ditt minne kan förbättras med rätt stöd. Här är vad forskningen rekommenderar.' : 'Dina svar indikerar ett mönster av försämring som kan bli värre om inget görs. Här är vad forskningen rekommenderar.'}
            </p>
          </div>
          <div className="flex flex-col md:flex-row gap-8 mb-8" data-id="element-188">
            <div className="md:w-1/2" data-id="element-189">
              <div className="aspect-w-4 aspect-h-3 mb-6" data-id="element-190">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/fWfifkPAXHppTAd7PLhURS/image.png" alt="Brainy kosttillskott" className="rounded-lg object-contain w-full h-full shadow-md" data-id="element-191" />
              </div>
              <div className="bg-red-50 border border-red-100 rounded-lg p-4 mb-4" data-id="element-192">
                <div className="flex items-center mb-2" data-id="element-193">
                  <AlertCircleIcon className="h-5 w-5 text-red-600 mr-2" data-id="element-194" />
                  <span className="font-semibold text-red-800" data-id="element-195">
                    Varningssignaler i din profil:
                  </span>
                </div>
                <p className="text-gray-700" data-id="element-196">
                  {severity === 'mild' ? 'Dina svar indikerar tidiga tecken på minnesförsämring som bör åtgärdas innan de blir värre. Detta mönster är vanligt hos personer över 40 år.' : 'Dina svar indikerar en accelererad försämring av minnesfunktioner som kan bli värre med tiden om inget görs. Detta mönster är vanligt hos 68% av personer över 40 år.'}
                </p>
              </div>
              <div className="flex items-center mb-2" data-id="element-197">
                <div className="flex" data-id="element-198">
                  {[1, 2, 3, 4, 5].map(star => <StarIcon key={star} className="h-5 w-5 text-yellow-400 fill-current" data-id="element-199" />)}
                </div>
                <span className="ml-2 text-gray-700 font-medium" data-id="element-200">
                  4.9/5 från över 300 000 användare
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-4" data-id="element-201">
                Kliniskt testad formula utvecklad under 8+ år
              </p>
            </div>
            <div className="md:w-1/2" data-id="element-202">
              <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center" data-id="element-203">
                <BrainIcon className="h-5 w-5 text-blue-600 mr-2" data-id="element-204" />
                Brainy - Återställer Ditt Minne På Naturlig Väg
              </h2>
              <ul className="space-y-3 mb-6" data-id="element-205">
                <li className="flex" data-id="element-206">
                  <CheckIcon className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-207" />
                  <span data-id="element-208">Stoppar åldersrelaterad minnesförlust</span>
                </li>
                <li className="flex" data-id="element-209">
                  <CheckIcon className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-210" />
                  <span data-id="element-211">Återaktiverar slumrande neurala kopplingar</span>
                </li>
                <li className="flex" data-id="element-212">
                  <CheckIcon className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-213" />
                  <span data-id="element-214">Förbättrar minnet redan inom 14 dagar</span>
                </li>
                <li className="flex" data-id="element-215">
                  <CheckIcon className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-216" />
                  <span data-id="element-217">Eliminerar "hjärndimma" och mental trötthet</span>
                </li>
                <li className="flex" data-id="element-218">
                  <CheckIcon className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-219" />
                  <span data-id="element-220">
                    Innehåller Bacopa Monnieri och andra kliniskt testade
                    ingredienser för minnesförbättring
                  </span>
                </li>
              </ul>
              <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-4 mb-6" data-id="element-221">
                <div className="flex items-center mb-2" data-id="element-222">
                  <ZapIcon className="h-5 w-5 text-yellow-600 mr-2" data-id="element-223" />
                  <span className="font-semibold text-yellow-800" data-id="element-224">
                    Viktigt att veta:
                  </span>
                </div>
                <p className="text-gray-700 text-sm" data-id="element-225">
                  Hjärnans förmåga att bilda nya neurala kopplingar avtar med
                  10% per årtionde efter 40 års ålder. Brainy innehåller
                  specifika näringsämnen som motverkar denna process och
                  stimulerar bildandet av nya kopplingar.
                </p>
              </div>
              <div className="text-center text-sm text-red-600 font-semibold mb-2" data-id="element-226">
                Begränsat erbjudande – gäller bara i 48 timmar
              </div>
              <a href="https://addrevenue.io/t?c=3462679&a=986619&l=165" className="block w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-6 rounded-lg text-center text-lg transition duration-300 transform hover:scale-105 shadow-md pulse-button" data-id="element-227">
                Se mitt personliga erbjudande
              </a>
              <p className="mt-3 text-sm text-center text-gray-600" data-id="element-228">
                180 dagars garanti • Gratis frakt • Säkra betalningar
                (Klarna/PayPal/Visa)
              </p>
              <div className="mt-6 p-4 border border-gray-100 rounded-lg bg-gray-50" data-id="element-229">
                <p className="italic text-gray-700" data-id="element-230">
                  "{featuredTestimonial.text}"
                </p>
                <p className="mt-2 text-sm text-gray-600 font-semibold" data-id="element-231">
                  – {featuredTestimonial.name},{' '}
                  {featuredTestimonial.occupation.split(',')[1]}
                </p>
              </div>
              <div className="mt-4 text-center text-sm text-gray-600" data-id="element-232">
                <p data-id="element-233">
                  91% av användare rapporterar förbättrat minne inom 14 dagar
                </p>
              </div>
            </div>
          </div>
        </div>
        <Testimonials data-id="element-234" />
        <div id="order" className="bg-white rounded-lg shadow-lg p-6 md:p-8 mt-8" data-id="element-235">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center" data-id="element-236">
            Återställ Ditt Minne Med Brainy
          </h2>
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-6 mb-6" data-id="element-237">
            <div className="flex items-center justify-between mb-4" data-id="element-238">
              <div className="font-semibold" data-id="element-239">
                Brainy - 60 kapslar (1 månads förbrukning)
              </div>
              <div className="flex items-center" data-id="element-240">
                <span className="text-gray-500 line-through mr-2" data-id="element-241">374 kr</span>
                <span className="font-bold text-blue-600" data-id="element-242">187 kr</span>
              </div>
            </div>
            <div className="flex items-center justify-between mb-4" data-id="element-243">
              <div className="text-gray-600" data-id="element-244">Frakt</div>
              <div className="font-semibold text-blue-600" data-id="element-245">Gratis</div>
            </div>
            <div className="border-t border-blue-200 pt-4" data-id="element-246">
              <div className="flex items-center justify-between" data-id="element-247">
                <div className="font-semibold" data-id="element-248">Totalt</div>
                <div className="font-bold text-xl" data-id="element-249">187 kr</div>
              </div>
            </div>
          </div>
          <div className="text-center text-sm text-red-600 font-semibold mb-2" data-id="element-250">
            Begränsat erbjudande – gäller bara i 48 timmar
          </div>
          <a href="https://addrevenue.io/t?c=3462679&a=986619&l=165" className="block w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-6 rounded-lg text-center text-lg transition duration-300 mb-4 shadow-md pulse-button" target="_blank" rel="noopener noreferrer" data-id="element-251">
            Beställ Brainy med 50% rabatt idag
          </a>
          <div className="text-center text-sm text-gray-600 mb-2" data-id="element-252">
            180 dagars garanti • Gratis frakt • Säkra betalningar
            (Klarna/PayPal/Visa)
          </div>
          <div className="text-center text-sm text-gray-500" data-id="element-253">
            <p data-id="element-254">
              Begränsat antal i lager - 91% upplever förbättrat minne inom 14
              dagar
            </p>
          </div>
        </div>
      </div>
    </div>;
};
export default QuizResults;